import { Activity, ArrowRight, Info, Timer } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Button, Card, Field, Input, Notice, PageHeader, Textarea } from '../components/UI'
import { getLocalDate } from '../lib/nutrition'
import { useMarlovaStore } from '../store'

function localDateTimeValue(iso: string): string {
  const date = new Date(iso)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

export function ActivityPage() {
  const navigate = useNavigate()
  const { activityId } = useParams()
  const activity = useMarlovaStore((state) => state.activities.find((entry) => entry.id === activityId))
  const addActivity = useMarlovaStore((state) => state.addActivity)
  const updateActivity = useMarlovaStore((state) => state.updateActivity)
  const [name, setName] = useState(activity?.name ?? '')
  const [calories, setCalories] = useState(activity ? String(activity.caloriesBurned) : '')
  const [duration, setDuration] = useState(activity?.durationMinutes ? String(activity.durationMinutes) : '')
  const [notes, setNotes] = useState(activity?.notes ?? '')
  const [dateTime, setDateTime] = useState(activity ? localDateTimeValue(activity.occurredAt) : localDateTimeValue(new Date().toISOString()))
  const [affectsTarget, setAffectsTarget] = useState(activity?.affectsDailyTarget ?? true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const editing = Boolean(activityId)

  if (editing && !activity) return <Navigate to="/today" replace />

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim() || Number(calories) <= 0) return
    const occurred = new Date(dateTime)
    setSaving(true); setError('')
    try {
      const values = {
        name: name.trim(), caloriesBurned: Number(calories), durationMinutes: duration ? Number(duration) : null,
        notes: notes.trim(), occurredAt: occurred.toISOString(), localDate: getLocalDate(occurred), affectsDailyTarget: affectsTarget,
      }
      if (activity) await updateActivity(activity.id, values)
      else await addActivity(values)
      navigate('/today')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'The activity could not be saved.') } finally { setSaving(false) }
  }

  return <div className="page narrow-page"><PageHeader eyebrow="Movement" title={editing ? 'Edit activity' : 'Add activity'} description={editing ? 'Update the movement details reflected in your daily target.' : 'Enter the calories you want Marlova to reflect today.'} backTo="/today" />
    <Card className="activity-form-card"><form onSubmit={submit}>
      <div className="activity-form-icon"><Activity /></div>{error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Activity name" required><Input value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Evening walk" /></Field>
      <div className="form-grid"><Field label="Calories burned" required><div className="input-with-suffix"><Input type="number" min="1" inputMode="numeric" value={calories} onChange={(event) => setCalories(event.target.value)} /><span>kcal</span></div></Field><Field label="Duration" hint="Optional"><div className="input-with-suffix"><Timer /><Input type="number" min="1" inputMode="numeric" value={duration} onChange={(event) => setDuration(event.target.value)} /><span>min</span></div></Field></div>
      <Field label="Date and time" required><Input type="datetime-local" value={dateTime} max={new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16)} onChange={(event) => setDateTime(event.target.value)} /></Field>
      <Field label="Notes" hint="Optional"><Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Anything useful to remember" /></Field>
      <div className="affects-target"><div><span><Info /></span><div><strong>Add calories to today’s target</strong><p>Your base goal already accounts for usual activity. Turn this off if adding it would count the same movement twice.</p></div></div><label className="switch"><input type="checkbox" checked={affectsTarget} onChange={(event) => setAffectsTarget(event.target.checked)} /><span /></label></div>
      <Notice tone="info">Your adjusted target will update immediately after saving.</Notice>
      <div className="form-actions"><Button type="button" variant="ghost" onClick={() => navigate('/today')}>Cancel</Button><Button type="submit" size="lg" loading={saving} disabled={saving || !name.trim() || Number(calories) <= 0}>{editing ? 'Save changes' : 'Save activity'} <ArrowRight /></Button></div>
    </form></Card>
  </div>
}
